// 버핏스코어 · 유사기업 조회의 공용 레이아웃 — 채팅의 좌측 사이드바를 유지한 채
// 우측 본문(<Outlet/>)만 바뀐다. 라우터의 layout route 로 쓰여서 버핏 ↔ 유사기업
// 전환 때 사이드바가 리마운트되지 않는다 (사용자 요청: 좌측 패널이 흔들리면 안 됨).
// 각 페이지는 자기 <header className="lab-top"> 와 .lab-scroll 본문을 렌더한다.
import { useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import Sidebar from "../chat/Sidebar";
import SideReopen from "../chat/SideReopen";
import ContactFooter from "../ContactFooter";
import { shellClass, useRailSettle } from "../../lib/useRailSettle";
import { initialCollapsed, isMobile, useMobileSide } from "../../lib/useMobileSide";
import { useAuth } from "../../stores/auth";
import { useChat } from "../../stores/chat";
import "../../styles/chat.css";
import "../../styles/lab.css";

export default function LabShell() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  // 시장 이슈는 3열(목록·상세·관련기업)을 화면 높이에 꽉 채우는 화면이라 본문을
  // 가로 배치로 바꾸고 하단 푸터를 두지 않는다. 버핏·유사기업은 그대로 세로 flex.
  const wide = pathname.startsWith("/market");
  // 버핏 스코어도 시안(v3)에서 좌 목록 · 우 조서를 화면 높이에 꽉 채우는 화면이
  // 됐다 — 세로 flex 는 그대로 두고 하단 푸터만 뺀다.
  const full = wide || pathname.startsWith("/buffett");
  const user = useAuth((s) => s.user);
  const sessions = useChat((s) => s.sessions);
  // 모바일이면 접고 시작한다 — 안 그러면 오버레이 사이드바가 화면을 덮은 채 뜬다
  const [collapsed, setCollapsed] = useState(initialCollapsed);

  // 대화 목록은 채팅과 같은 스토어 — 채팅을 거치지 않고 진입해도 목록이 차게 한다
  useEffect(() => {
    useChat.getState().init(user);
  }, [user]);

  const { railSettled, sideAnimating } = useRailSettle(collapsed);
  // 바깥 클릭·리사이즈로 접기. 저장하지 않는다 — 모바일에서 잠깐 닫은 것이
  // 데스크톱 취향으로 굳으면 안 된다.
  useMobileSide(collapsed, () => setCollapsed(true));

  // 섹션을 옮기면 모바일 오버레이를 닫는다. 사이드바 안의 버핏·유사기업·시장 이슈
  // 링크는 Sidebar 가 자체 navigate 로 이동해서 콜백으로는 못 잡는다 — 게다가
  // 바깥 클릭 핸들러도 사이드바 안은 예외라, 경로 변화로 닫는 것이 유일하게 확실하다.
  useEffect(() => {
    if (isMobile()) setCollapsed(true);
  }, [pathname]);

  const setCollapsedPersist = (on: boolean) => {
    setCollapsed(on);
    try {
      localStorage.setItem("va_side_collapsed", on ? "1" : "0");
    } catch {
      /* noop */
    }
  };

  return (
    <div className="chat-root lab-shell">
      <div className={shellClass(collapsed, railSettled, sideAnimating)}>
        <Sidebar
          sessions={sessions}
          activeId={null}
          writingIds={[]}
          busy={false}
          onNew={() => navigate("/")}
          onNewInGroup={() => navigate("/")}
          onOpen={(id) => navigate(`/c/${encodeURIComponent(id)}`)}
          onDelete={(id) => {
            const s = sessions.find((x) => x.id === id);
            if (!window.confirm("이 대화를 삭제할까요?" + (s?.title ? "\n\n" + s.title : ""))) return;
            useChat.getState().deleteSession(id);
          }}
          onOpenLibrary={() => navigate("/library")}
          libraryOpen={false}
          railMode={railSettled}
          onCollapse={() => setCollapsedPersist(true)}
          onExpand={() => setCollapsedPersist(false)}
          onResizingChange={() => {}}
          onRenamed={() => {}}
          onNavigated={() => {
            // 같은 섹션을 다시 눌러도 닫는다 — pathname 감시(아래 effect)는 못 잡는 경우
            if (isMobile()) setCollapsed(true);
          }}
        />
        <main className={`main lab-main${wide ? " mk-main" : ""}`}>
          <Outlet />
          {!full && <ContactFooter />}
        </main>
      </div>
      <SideReopen collapsed={collapsed} onOpen={() => setCollapsedPersist(false)} />
    </div>
  );
}
